import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import {
  fetchAccountBillabilityAction,
  type BillabilityResult,
} from "../slices/accountBillabilitySlice";

export function useAccountBillability(accountIds: string[]) {
  const dispatch = useDispatch();
  const { billability, loading, error } = useSelector(
    (state: RootState) => state.accountBillability,
  );

  // Stable string key from the accountIds values (not the array reference).
  const accountIdsKey = accountIds.filter(Boolean).join(",");

  useEffect(() => {
    if (accountIdsKey === "") return;
    // Always refetch when the visible set of accountIds changes (or on
    // remount). We intentionally do NOT skip "already cached" IDs — Redux
    // state survives route changes, so the cache can be stale after the
    // user edits a Company in another route (e.g. setting
    // leadGenPaymentSourceType) and returns to this page.
    dispatch(fetchAccountBillabilityAction(accountIdsKey.split(",")));
  }, [accountIdsKey, dispatch]);

  const getBillability = useCallback(
    (accountId: string): BillabilityResult | undefined =>
      billability[accountId],
    [billability],
  );

  const isBillable = useCallback(
    (accountId: string): boolean | undefined => {
      const result = billability[accountId];
      if (result === undefined) return undefined;
      return result.status === "billable";
    },
    [billability],
  );

  const getBillabilityMessage = useCallback(
    (accountId: string): string => {
      const result = billability[accountId];
      if (result?.status === "no_account") {
        return `Account ${accountId} does not exist in CARA.`;
      }
      return `Account ${accountId} has no payment method on file in CARA, and no LeadGen default is set on the Company. Set "Default LeadGen Payment Source" on the company edit screen, or wait for the customer to enter their payment method in CARA.`;
    },
    [billability],
  );

  return {
    isBillable,
    getBillability,
    getBillabilityMessage,
    loading,
    error,
  };
}
